import { CommonModule } from '@angular/common';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { forkJoin } from 'rxjs';

import { AppIcons } from '../../../core/icons/fa-icons';
import { AdminService } from '../shared/service/admin.service';

@Component({
  selector: 'app-role-assignment',
  standalone: true,
  imports: [TranslatePipe, CommonModule, FormsModule],
  templateUrl: './role-assignment.html',
  styleUrl: './role-assignment.css',
})
export class RoleAssignment implements OnInit {
  AppIcons = AppIcons;

  roles: any[] = [];

  selectedRole: any = null;

  permissionModules: any[] = [];

  loading = false;

  constructor(
    private translate: TranslateService,
    private adminService: AdminService,
    private toastr: ToastrService,
  ) {}

  ngOnInit(): void {
    this.getSubModules();

    this.getRoles();
  }

  // =====================================================
  // GET ROLES
  // =====================================================

  getRoles(): void {
    this.adminService.getAllRoles().subscribe({
      next: (res: any) => {
        const data = res?.data || res || [];

        this.roles = data.map((item: any) => ({
          id: item.userRoleSeq,
          name: item.userRoleId,
          description: item.userRoleName,
          modules: 0,
        }));

        if (this.roles.length) {
          this.selectedRole = this.roles[0];

          setTimeout(() => {
            this.loadRolePermissions(this.selectedRole.id);
          }, 500);
        }
      },

      error: () => {
        this.toastr.error(this.translate.instant('SETUP.ROLES.LOAD_ROLES_FAILED'));
      },
    });
  }

  // =====================================================
  // GET MODULES
  // =====================================================

  getSubModules(): void {
    forkJoin({
      modules: this.adminService.getAllModes(),
      subModules: this.adminService.getAllSubModes(),
    }).subscribe({
      next: ({ modules, subModules }: any) => {
        const moduleData = modules?.data || modules || [];

        const subModuleData = subModules?.data || subModules || [];

        const grouped: any[] = [];

        moduleData.forEach((mod: any) => {
          const permissions = subModuleData
            .filter((x: any) => x.modSeq == mod.modSeq)
            .map((item: any) => ({
              appAuthSeq: 0,

              subModuleSeq: item.sbModSeq,

              label: item.sbModNm,

              route: item.sbModUrl,

              allowed: false,
            }));

          grouped.push({
            modSeq: mod.modSeq,

            name: mod.modNm,

            expanded: true,

            permissions,
          });
        });

        this.permissionModules = grouped;
      },

      error: () => {
        this.toastr.error(this.translate.instant('SETUP.ROLES.LOAD_MODULES_FAILED'));
      },
    });
  }

  // =====================================================
  // SELECT ROLE
  // =====================================================

  selectRole(role: any): void {
    this.selectedRole = role;

    this.loadRolePermissions(role.id);
  }

  // =====================================================
  // LOAD ROLE PERMISSIONS
  // =====================================================

  loadRolePermissions(roleId: number): void {
    this.adminService.getAuthListForRole(roleId).subscribe({
      next: (res: any) => {
        const permissions = res?.data || res || [];

        this.permissionModules.forEach((module: any) => {
          module.permissions.forEach((perm: any) => {
            const found = permissions.find((x: any) => x.sbModSeq == perm.subModuleSeq);

            if (found) {
              perm.appAuthSeq = found.authSeq;

              perm.allowed = true;
            } else {
              perm.appAuthSeq = 0;

              perm.allowed = false;
            }

            // STORE ORIGINAL STATE
            perm.originalAllowed = perm.allowed;
          });
        });
      },

      error: () => {
        this.toastr.error(this.translate.instant('SETUP.ROLES.LOAD_PERMISSIONS_FAILED'));
      },
    });
  }

  // =====================================================
  // SAVE
  // =====================================================

  savePermissions(): void {
    if (!this.selectedRole) {
      this.toastr.error(this.translate.instant('SETUP.ROLES.SELECT_ROLE'));

      return;
    }

    const payload: any[] = [];

    this.permissionModules.forEach((module: any) => {
      module.permissions.forEach((perm: any) => {
        // ONLY CHANGED ITEMS
        if (perm.allowed !== perm.originalAllowed) {
          // ===============================
          // CHECKED
          // ===============================
          if (perm.allowed) {
            payload.push({
              userRoleSeq: this.selectedRole.id,

              subModuleSeq: perm.subModuleSeq,

              delFlg: false,
            });
          }

          // ===============================
          // UNCHECKED
          // ===============================
          else {
            // only if already exists
            if (perm.appAuthSeq > 0) {
              payload.push({
                appAuthSeq: perm.appAuthSeq,

                userRoleSeq: this.selectedRole.id,

                subModuleSeq: perm.subModuleSeq,

                delFlg: true,
              });
            }
          }
        }
      });
    });

    if (!payload.length) {
      this.toastr.warning(this.translate.instant('SETUP.ROLES.NO_CHANGES_FOUND'));

      return;
    }

    this.loading = true;

    console.log(payload);

    this.adminService.saveAppAccess(payload).subscribe({
      next: () => {
        this.toastr.success(this.translate.instant('SETUP.ROLES.PERMISSIONS_UPDATED'));

        this.loading = false;

        this.loadRolePermissions(this.selectedRole.id);
        this.adminService.loadUserPermissions();
      },

      error: () => {
        this.toastr.error(this.translate.instant('SETUP.ROLES.UPDATE_PERMISSIONS_FAILED'));

        this.loading = false;
      },
    });
  }
  // =====================================================
  // COUNT
  // =====================================================

  getAllowedCount(permissions: any[]): number {
    return permissions.filter((x: any) => x.allowed).length;
  }

  // =====================================================
  // ACTIONS
  // =====================================================

  openRolePopup(): void {
    console.log('Open Add Role Popup');
  }

  editRole(role: any): void {
    console.log('Edit Role', role);
  }

  deleteRole(role: any): void {
    console.log('Delete Role', role);
  }
}
